import { clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { generatePoints, type PointDistribution } from '../../core/pointSources';
import { IncrementalHash } from '../../core/spatialHash';
import type { RenderTarget, TechniqueContext } from '../types';
import type { SpaceColonizationValues } from './params';

/**
 * Space colonisation (Runions et al., "Modeling Trees with a Space Colonization
 * Algorithm", 2007) — the algorithm behind leaf venation and branching networks.
 *
 * Each iteration: every attractor picks its nearest node; every node that was picked
 * grows one segment towards the average direction of its attractors; attractors within
 * the kill distance are consumed. The network therefore *fills* the space the
 * attractors occupy rather than following a prescribed shape, which is why the
 * distribution parameter is where the composition is really decided.
 *
 * The nearest-node query is the whole cost, so nodes go into an incremental spatial
 * hash as they are created.
 */
export interface ScNode {
  x: number;
  y: number;
  parent: number;
  order: number;
  bornAt: number;
  width: number;
}

export interface SpaceColonizationState {
  nodes: ScNode[];
  attractors: Float32Array;
  alive: Uint8Array;
  remaining: number;
  hash: IncrementalHash;
  palette: ResolvedPalette;
  iterations: number;
  maxOrder: number;
  done: boolean;
}

function values(ctx: TechniqueContext): SpaceColonizationValues {
  return ctx.values as unknown as SpaceColonizationValues;
}

export function init(ctx: TechniqueContext): SpaceColonizationState {
  const v = values(ctx);
  const attractors = generatePoints(v.attractors, v.distribution as PointDistribution, {
    field: ctx.field,
    width: ctx.width,
    height: ctx.height,
    rng: ctx.rng,
    distance: v.distributionDistance,
  });

  const state: SpaceColonizationState = {
    nodes: [],
    attractors,
    alive: new Uint8Array(attractors.length / 2).fill(1),
    remaining: attractors.length / 2,
    hash: new IncrementalHash(ctx.width, ctx.height, Math.max(8, v.attractionDistance / 2)),
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    iterations: 0,
    maxOrder: 1,
    done: false,
  };

  const addRoot = (x: number, y: number): void => {
    const id = state.nodes.length;
    state.nodes.push({ x, y, parent: -1, order: 0, bornAt: 0, width: v.baseWidth });
    state.hash.insert(x, y, id);
  };

  switch (v.origin) {
    case 'bottom':
      for (let i = 0; i < v.origins; i++) addRoot(((i + 0.5) / v.origins) * ctx.width, ctx.height - 1);
      break;
    case 'border':
      for (let i = 0; i < v.origins; i++) {
        const t = (i + 0.5) / v.origins;
        const side = i % 4;
        if (side === 0) addRoot(t * ctx.width, 1);
        else if (side === 1) addRoot(ctx.width - 1, t * ctx.height);
        else if (side === 2) addRoot(t * ctx.width, ctx.height - 1);
        else addRoot(1, t * ctx.height);
      }
      break;
    case 'centre':
      for (let i = 0; i < v.origins; i++) {
        const a = (i / v.origins) * Math.PI * 2;
        addRoot(ctx.width / 2 + Math.cos(a) * 4, ctx.height / 2 + Math.sin(a) * 4);
      }
      break;
    default: {
      const samples = ctx.field.sampleOutline(v.origins);
      for (const s of samples) addRoot(s.x + s.nx * 2, s.y + s.ny * 2);
    }
  }

  if (state.nodes.length === 0) addRoot(ctx.width / 2, ctx.height - 1);
  return state;
}

export function step(state: SpaceColonizationState, _dt: number, ctx: TechniqueContext): SpaceColonizationState {
  if (state.done) return state;
  const v = values(ctx);

  const rounds = Math.max(1, Math.round(4 * clamp01(ctx.quality)));
  for (let round = 0; round < rounds; round++) {
    if (state.remaining === 0 || state.iterations >= v.maxIterations) {
      state.done = true;
      return state;
    }
    state.iterations++;

    // ── Associate attractors with their nearest node ──────────────────────
    const dirX = new Map<number, number>();
    const dirY = new Map<number, number>();
    const counts = new Map<number, number>();

    for (let a = 0; a < state.alive.length; a++) {
      if (!state.alive[a]) continue;
      const ax = state.attractors[a * 2];
      const ay = state.attractors[a * 2 + 1];

      let best = -1;
      let bestDist = v.attractionDistance;
      state.hash.query(ax, ay, v.attractionDistance, (id) => {
        const d = Math.hypot(state.nodes[id].x - ax, state.nodes[id].y - ay);
        if (d < bestDist) {
          bestDist = d;
          best = id;
        }
        return true;
      });
      if (best < 0) continue;

      if (bestDist <= v.killDistance) {
        state.alive[a] = 0;
        state.remaining--;
        continue;
      }

      const node = state.nodes[best];
      const len = bestDist || 1;
      dirX.set(best, (dirX.get(best) ?? 0) + (ax - node.x) / len);
      dirY.set(best, (dirY.get(best) ?? 0) + (ay - node.y) / len);
      counts.set(best, (counts.get(best) ?? 0) + 1);
    }

    if (counts.size === 0) {
      state.done = true;
      return state;
    }

    // ── Grow one segment per influenced node ──────────────────────────────
    for (const [id, count] of counts) {
      const node = state.nodes[id];
      let dx = (dirX.get(id) ?? 0) / count;
      let dy = (dirY.get(id) ?? 0) / count;
      const len = Math.hypot(dx, dy);
      if (len < 1e-6) continue;
      dx /= len;
      dy /= len;

      const nx = node.x + dx * v.segmentLength;
      const ny = node.y + dy * v.segmentLength;
      if (v.avoidText && ctx.field.isInside(nx, ny)) continue;
      if (nx < -40 || ny < -40 || nx > ctx.width + 40 || ny > ctx.height + 40) continue;

      const childId = state.nodes.length;
      const order = node.order + 1;
      state.nodes.push({
        x: nx,
        y: ny,
        parent: id,
        order,
        bornAt: state.iterations,
        width: Math.max(0.2, node.width * v.widthFalloff ** 0.12),
      });
      if (order > state.maxOrder) state.maxOrder = order;
      state.hash.insert(nx, ny, childId);
    }
  }

  return state;
}

export function isComplete(state: SpaceColonizationState): boolean {
  return state.done;
}

export function draw(state: SpaceColonizationState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const reach = Math.max(1, ctx.field.maxOutside);
  c.lineCap = 'round';

  for (const node of state.nodes) {
    if (node.parent < 0) continue;
    const parent = state.nodes[node.parent];

    let t: number;
    switch (v.colorMode) {
      case 'order':
        t = node.order / Math.max(1, state.maxOrder);
        break;
      case 'distance':
        t = clamp01(Math.abs(ctx.field.distance(node.x, node.y)) / reach);
        break;
      case 'time':
        t = node.bornAt / Math.max(1, state.iterations);
        break;
      default:
        t = 0;
    }
    c.strokeStyle = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);
    // Thicker near the root: width falls off with branch order.
    c.lineWidth = Math.max(0.2, v.baseWidth * v.widthFalloff ** (node.order * 0.14));
    c.beginPath();
    c.moveTo(parent.x, parent.y);
    c.lineTo(node.x, node.y);
    c.stroke();
  }
}

export function elementCount(state: SpaceColonizationState): number {
  return state.nodes.length;
}

export function toSVG(state: SpaceColonizationState, ctx: TechniqueContext): string {
  const v = values(ctx);
  const f = (n: number): string => n.toFixed(2).replace(/\.?0+$/, '');
  const reach = Math.max(1, ctx.field.maxOutside);
  const parts: string[] = [`<g fill="none" stroke-linecap="round" stroke-opacity="${v.opacity}">`];

  for (const node of state.nodes) {
    if (node.parent < 0) continue;
    const parent = state.nodes[node.parent];
    let t: number;
    switch (v.colorMode) {
      case 'order':
        t = node.order / Math.max(1, state.maxOrder);
        break;
      case 'distance':
        t = clamp01(Math.abs(ctx.field.distance(node.x, node.y)) / reach);
        break;
      case 'time':
        t = node.bornAt / Math.max(1, state.iterations);
        break;
      default:
        t = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0) : state.palette.at(t);
    const width = Math.max(0.2, v.baseWidth * v.widthFalloff ** (node.order * 0.14));
    parts.push(
      `<line x1="${f(parent.x)}" y1="${f(parent.y)}" x2="${f(node.x)}" y2="${f(node.y)}" stroke="${color}" stroke-width="${f(width)}"/>`,
    );
  }

  parts.push('</g>');
  return parts.join('\n');
}
